// src/pages/payment/PaymentSuccess.jsx
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { 
  CheckCircle, 
  Package, 
  Truck, 
  Mail, 
  ArrowRight,
  Download,
  Share2,
  Clock,
  MapPin, Phone
} from 'lucide-react';

// Header is now in PublicLayout
import { usePayment } from '../../features/october/hooks/usePayment';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getOrder, handlePaymentSuccess, isProcessing, error } = usePayment();
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const handlePaymentReturn = async () => {
      try {
        setIsLoading(true);

        const orderId = searchParams.get('order');
        if (!orderId) {
          navigate('/kganya-scents');
          return;
        }

        // Fetch the order
        const orderData = await getOrder(orderId);

        // PayFast does not send payment_status to the return_url — it is only sent
        // via the ITN (notify_url). Since the user lands here the payment succeeded,
        // so if the order is still pending we mark it complete and clear the cart.
        if (orderData.paymentStatus !== 'completed') {
          const updatedOrder = await handlePaymentSuccess(orderId, {
            transactionId: searchParams.get('pf_payment_id') || null,
            paymentReference: searchParams.get('m_payment_id') || orderId,
          });
          setOrder(updatedOrder);
        } else {
          setOrder(orderData);
        }
      } catch (err) {
        console.error('Error handling payment return:', err);
        navigate('/payment/failed');
      } finally {
        setIsLoading(false);
      }
    };

    handlePaymentReturn();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownloadReceipt = () => {
    if (!order) return;
    
    const receiptData = {
      orderNumber: order.orderNumber,
      date: new Date(order.createdAt).toLocaleDateString(),
      customer: order.customer,
      items: order.items,
      total: order.total,
      currency: order.currency
    };
    
    const dataStr = JSON.stringify(receiptData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `pontso-yarona-receipt-${order.orderNumber}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleShareOrder = () => {
    if (!order) return;
    
    const shareData = {
      title: 'My Pontso Yarona Order',
      text: `I just ordered from Pontso Yarona! Order #${order.orderNumber}`,
      url: window.location.href
    };
    
    if (navigator.share) {
      navigator.share(shareData);
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(`Check out my Pontso Yarona order: ${window.location.href}`);
    }
  };

  if (isLoading || isProcessing) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full mx-auto mb-4"
          />
          <p className="text-white font-body font-light">Processing your order...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4 font-body">Something went wrong processing your order.</p>
          <Link 
            to="/kganya-scents" 
            className="text-brand-gold hover:text-brand-gold-light underline font-body"
          >
            Return to Shop
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-dark">
      <div className="pt-20 pb-16 bg-brand-dark min-h-screen">
        <div className="max-w-4xl mx-auto px-4">
          
          {/* Success Animation */}
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, ease: "backOut" }}
            className="text-center mb-12"
          >
            <div className="relative mb-6">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="w-20 h-20 bg-brand-gold rounded-full flex items-center justify-center mx-auto"
              >
                <CheckCircle size={40} className="text-white" />
              </motion.div>
              
              {/* Animated rings */}
              <motion.div
                initial={{ scale: 0, opacity: 1 }}
                animate={{ scale: 2, opacity: 0 }}
                transition={{ delay: 0.5, duration: 1.5 }}
                className="absolute inset-0 w-20 h-20 border-2 border-green-500 rounded-full mx-auto"
              />
              <motion.div
                initial={{ scale: 0, opacity: 1 }}
                animate={{ scale: 3, opacity: 0 }}
                transition={{ delay: 0.7, duration: 1.5 }}
                className="absolute inset-0 w-20 h-20 border-2 border-green-500 rounded-full mx-auto"
              />
            </div>
            
            <motion.h1
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-3xl lg:text-4xl font-light text-white mb-4 tracking-wide"
            >
              Payment Successful!
            </motion.h1>
            
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-gray-400 font-light text-lg"
            >
              Thank you for your order. We'll send you a confirmation email shortly.
            </motion.p>
          </motion.div>

          {/* Order Details */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="bg-black/90 backdrop-blur-xl rounded-3xl border border-white/10 p-8 mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-light text-white tracking-wide">Order Details</h2>
              <span className="px-4 py-2 bg-green-500/20 text-green-400 rounded-xl font-light text-sm border border-green-500/30">
                {order.status}
              </span>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-white font-light mb-4">Order Information</h3>
                <div className="space-y-3 text-gray-400 font-light">
                  <div className="flex justify-between">
                    <span>Order Number:</span>
                    <span className="text-white">{order.orderNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Date:</span>
                    <span className="text-white">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Payment Method:</span>
                    <span className="text-white">PayFast</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total:</span>
                    <span className="text-white text-lg">R{order.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-white font-light mb-4">Shipping Address</h3>
                <div className="text-gray-400 font-light">
                  <p className="text-white">{order.customer.firstName} {order.customer.lastName}</p>
                  <p>{order.shippingAddress.address}</p>
                  <p>{order.shippingAddress.city}, {order.shippingAddress.province}</p>
                  <p>{order.shippingAddress.postalCode}</p>
                  <p>South Africa</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Order Items */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="bg-black/90 backdrop-blur-xl rounded-3xl border border-white/10 p-8 mb-8"
          >
            <h2 className="text-xl font-light text-white mb-6 tracking-wide">Items Ordered</h2>
            
            <div className="space-y-4">
              {order.items.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.7 + index * 0.1 }}
                  className="flex items-center gap-4 bg-white/5 rounded-2xl p-4"
                >
                  <img 
                    src={item.image} 
                    alt={item.name}
                    className="w-16 h-16 object-contain rounded-xl bg-white/10"
                  />
                  <div className="flex-1">
                    <h3 className="text-white font-light">{item.name}</h3>
                    <p className="text-gray-400 text-sm">{item.category} • {item.type}</p>
                    <p className="text-gray-400 text-sm">Quantity: {item.quantity}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-light">{item.price}</p>
                    <p className="text-gray-400 text-sm">R{item.subtotal.toFixed(2)}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Next Steps */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="bg-black/90 backdrop-blur-xl rounded-3xl border border-white/10 p-8 mb-8"
          >
            <h2 className="text-xl font-light text-white mb-6 tracking-wide">What's Next?</h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center border border-yellow-500/30">
                  <Mail size={20} className="text-yellow-400" />
                </div>
                <div>
                  <h3 className="text-white font-light mb-2">Processing</h3>
                  <p className="text-gray-400 font-light text-sm">
                    Your order is being prepared for shipment
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center border border-green-500/30">
                  <Truck size={20} className="text-green-400" />
                </div>
                <div>
                  <h3 className="text-white font-light mb-2">Shipping</h3>
                  <p className="text-gray-400 font-light text-sm">
                    Free delivery within 2-5 business days
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-8 p-6 bg-blue-500/10 rounded-2xl border border-blue-500/20">
              <div className="flex items-center gap-3 mb-3">
                <Clock size={20} className="text-blue-400" />
                <h3 className="text-white font-light">Estimated Delivery</h3>
              </div>
              <p className="text-blue-200 font-light">
                Your order should arrive between{' '}
                <span className="text-white">
                  {new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                </span>
                {' '}and{' '}
                <span className="text-white">
                  {new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                </span>
              </p>
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.9 }}
            className="flex flex-wrap gap-4 justify-center"
          >
            <button
              onClick={handleDownloadReceipt}
              className="flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-2xl font-light transition-colors border border-white/20"
            >
              <Download size={16} />
              Download Receipt
            </button>
            
            <button
              onClick={handleShareOrder}
              className="flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-2xl font-light transition-colors border border-white/20"
            >
              <Share2 size={16} />
              Share Order
            </button>
            
            <Link
              to="/kganya-scents"
              className="flex items-center gap-2 px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-black rounded-2xl font-light transition-colors"
            >
              Continue Shopping
              <ArrowRight size={16} />
            </Link>
          </motion.div>

          {/* Customer Support */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.0 }}
            className="mt-12 text-center"
          >
            <div className="bg-black/90 backdrop-blur-xl rounded-3xl border border-white/10 p-8">
              <h3 className="text-white font-light mb-4">Need Help?</h3>
              <p className="text-gray-400 font-light mb-6">
                If you have any questions about your order, feel free to contact us.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <a
                  href="mailto:support@pontsoyarona.com"
                  className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl font-light transition-colors text-sm"
                >
                  <Mail size={14} />
                  Email Support
                </a>
                <a
                  href="tel:+27123456789"
                  className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl font-light transition-colors text-sm"
                >
                  <Phone size={14} />
                  Call Us
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
               